import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import Data from "../../../apiData/blog.json";
import { BlogListMeta } from "./BlogSeoMeta";
import LotusArt from "./BlogNoImage";
import useBlogAnalytics from "./useBlogAnalytics";
import {
  Page, BgBlob, DotGrid, Inner,
  HeroWrap, HeroEyebrow, HeroTitle, HeroDivider, HeroDesc,
  FilterBar, FilterBtn,
  CardsGrid, HeroCard, HeroCardImg, HeroCardBody,
  Card, CardImg, CardBody,
  CategoryPill, CardTitle, CardMeta, CardExcerpt, ReadMoreLink,
  VideoBadge, TagsRow, Tag, EmptyState, NoImageArt,
} from "./Blog.styles";

// ─── Constants ─────────────────────────────────────────────────────────────────

const ROOT_PATH = process.env.PUBLIC_URL + "/assets/ImagePackages";

const ytThumb = (id) => `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;

const imgSrc = (post) => {
  if (post.thumbnail?.startsWith("https://")) return post.thumbnail;
  if (post.asnVideoPath) return ytThumb(post.asnVideoPath);
  if (post.thumbnail) return `${ROOT_PATH}/${post.thumbnail}`;
  return null; // no image → fallback art is rendered instead
};

const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

// ─── Icon atoms ────────────────────────────────────────────────────────────────

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

// ─── PostImage ────────────────────────────────────────────────────────────────

const PostImage = ({ post, eager }) => {
  const src = imgSrc(post);
  const [failed, setFailed] = useState(false);
  const showArt = !src || failed;

  return (
    <>
      {showArt ? (
        <NoImageArt role="img" aria-label={post.title}>
          <LotusArt />
        </NoImageArt>
      ) : (
        <img
          src={src}
          alt={post.title}
          loading={eager ? "eager" : "lazy"}
          onError={() => setFailed(true)}
        />
      )}
      {post.asnVideoPath && (
        <VideoBadge aria-label="Video post">
          <span><PlayIcon /></span>
        </VideoBadge>
      )}
    </>
  );
};

// ─── Blog ─────────────────────────────────────────────────────────────────────

const Blog = () => {
  const analytics = useBlogAnalytics();
  const [activeCategory, setActiveCategory] = useState(null);

  // ── Page view on mount ──
  useEffect(() => {
    analytics.trackPageView("Blog Listing", window.location.href);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(
    () => [...new Set(Data.map((p) => p.category))].sort(),
    []
  );

  const posts = useMemo(() => {
    const filtered = activeCategory
      ? Data.filter((p) => p.category === activeCategory)
      : Data;
    return [...filtered].sort((a, b) => Number(b.sN) - Number(a.sN));
  }, [activeCategory]);

  const handleFilter = useCallback((cat) => {
    setActiveCategory((prev) => {
      const next = prev === cat ? null : cat;
      analytics.trackCategoryFilter(next);
      return next;
    });
  }, [analytics]);

  const handleCardClick = useCallback((post, isFeatured = false) => {
    analytics.trackCardClick(post, isFeatured);
    scrollToTop();
  }, [analytics]);

  const [featuredPost, ...restPosts] = posts;

  return (
    <Page>

      {/* ── SEO — BlogListMeta mirrors YogPageMeta pattern ── */}
      <BlogListMeta
        posts={posts}
        featuredImgUrl={featuredPost ? imgSrc(featuredPost) : null}
      />

      <BgBlob className="b1" aria-hidden="true" />
      <BgBlob className="b2" aria-hidden="true" />
      <BgBlob className="b3" aria-hidden="true" />
      <DotGrid aria-hidden="true" />

      {/* ── Hero header ── */}
      <HeroWrap>
        <HeroEyebrow>✦ YogaShastra Blog</HeroEyebrow>
        <HeroTitle>Wisdom, Wellness & Yoga</HeroTitle>
        <HeroDivider aria-hidden="true" />
        <HeroDesc>
          Explore insights on yoga philosophy, therapeutic practices, and
          holistic well-being — written by our experienced instructors.
        </HeroDesc>
      </HeroWrap>

      <Inner>
        {/* ── Category filter ── */}
        <FilterBar role="group" aria-label="Filter posts by category">
          <FilterBtn
            $active={!activeCategory}
            onClick={() => {
              setActiveCategory(null);
              analytics.trackCategoryFilter(null);
            }}
            aria-pressed={!activeCategory}
          >
            All Posts
          </FilterBtn>
          {categories.map((cat) => (
            <FilterBtn
              key={cat}
              $active={activeCategory === cat}
              onClick={() => handleFilter(cat)}
              aria-pressed={activeCategory === cat}
            >
              {cat}
            </FilterBtn>
          ))}
        </FilterBar>

        {posts.length === 0 ? (
          <EmptyState>
            <span className="icon">🧘</span>
            <h3>No posts found</h3>
            <p>Try selecting a different category.</p>
          </EmptyState>
        ) : (
          <CardsGrid>
            {/* ── Featured hero card ── */}
            {featuredPost && (
              <HeroCard
                as={Link}
                to={`/blog/${featuredPost.sN}`}
                onClick={() => handleCardClick(featuredPost, true)}
                aria-label={`Read featured post: ${featuredPost.title}`}
              >
                <HeroCardImg>
                  <PostImage post={featuredPost} eager />
                </HeroCardImg>
                <HeroCardBody>
                  <CategoryPill>{featuredPost.category}</CategoryPill>
                  <CardTitle $hero>{featuredPost.title}</CardTitle>
                  <CardMeta>
                    <span>{featuredPost.author}</span>
                    <span className="dot">•</span>
                    <span>{featuredPost.date}</span>
                  </CardMeta>
                  <CardExcerpt $lines={4}>{featuredPost.shortDescription}</CardExcerpt>
                  {featuredPost.seo?.tags?.length > 0 && (
                    <TagsRow>
                      {featuredPost.seo.tags.slice(0, 4).map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </TagsRow>
                  )}
                  <ReadMoreLink style={{ marginTop: "16px" }}>
                    Read Full Article <ArrowIcon />
                  </ReadMoreLink>
                </HeroCardBody>
              </HeroCard>
            )}

            {/* ── Remaining cards ── */}
            {restPosts.map((post, i) => (
              <Card
                key={post.sN}
                as={Link}
                to={`/blog/${post.sN}`}
                onClick={() => handleCardClick(post, false)}
                $index={i}
                aria-label={`Read: ${post.title}`}
              >
                <CardImg>
                  <PostImage post={post} />
                </CardImg>
                <CardBody>
                  <CategoryPill>{post.category}</CategoryPill>
                  <CardTitle>{post.title}</CardTitle>
                  <CardMeta>
                    <span>{post.author}</span>
                    <span className="dot">•</span>
                    <span>{post.date}</span>
                  </CardMeta>
                  <CardExcerpt>{post.shortDescription}</CardExcerpt>
                  {post.seo?.tags?.length > 0 && (
                    <TagsRow>
                      {post.seo.tags.slice(0, 3).map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </TagsRow>
                  )}
                  <ReadMoreLink>
                    Read More <ArrowIcon />
                  </ReadMoreLink>
                </CardBody>
              </Card>
            ))}
          </CardsGrid>
        )}
      </Inner>
    </Page>
  );
};

Blog.displayName = "Blog";
export default React.memo(Blog);